import { RefreshCcw } from 'lucide-react';
import React from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  H2,
  NewButton,
  P,
  useToast,
} from '@eluve/components';
import {
  useAppointmentId,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import { useAppointmentActivity } from '@eluve/frontend-appointment-hooks';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { useDialog } from '@eluve/utility-hooks';

import { TemplateSelectorGuard } from './TemplateSelectorGuard';
import { useRegenerateSummary } from './useRegenerateSummary';

export interface RegenerateSummaryButtonProps {}

export const RegenerateSummaryButton: React.FC<
  RegenerateSummaryButtonProps
> = () => {
  const toast = useToast();
  const logger = useNamedLogger('RegenerateSummaryButton');
  const { isDialogOpen, toggleDialog, setIsDialogOpen } = useDialog();

  const userId = useUserIdFromSession();
  const tenantId = useTenantIdFromParams();
  const currentSummary = useSummary();
  const appointmentId = useAppointmentId();

  const {
    selectedTemplates,
    setSelectedTemplates,
    initialPromptTemplateId,
    initialLlmOutputTemplateId,
    regenerateSummary,
  } = useRegenerateSummary();

  const captureEvent = useCaptureEvent({
    tenantId,
    appointmentId,
    llmOutputId: currentSummary.llmOutputId,
  });

  const { isSummarizationInProgress, isTranscriptionInProgress } =
    useAppointmentActivity();

  const regenerationDisabled =
    isSummarizationInProgress || isTranscriptionInProgress;

  const handleRegenerate = async () => {
    toggleDialog();

    logger.info('Regenerate Summary Button Clicked', {
      userId,
      tenantId,
      appointmentId,
      currrentSummaryLlmOutputId: currentSummary.llmOutputId,
    });

    captureEvent('user_regenerated_note_summary', {
      llmOutputId: currentSummary.llmOutputId,
    });

    await regenerateSummary(
      () => {
        toast.success('Regenerating your summary', {
          description: 'Your new summary should be available soon',
        });
      },
      () => {
        toast.error('Failed to regenerate summary', {
          description: 'Please try again',
        });
      },
    );
  };

  return (
    <>
      <NewButton
        disabled={regenerationDisabled}
        icon={{ name: 'RefreshCcw' }}
        onClick={() => {
          captureEvent('user_opened_regenerate_note_summary_dialog');
          toggleDialog();
        }}
        size="s"
        text="Regenerate"
        type="outline"
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="flex flex-col gap-5 p-8">
          <H2>Regenerating your summary</H2>
          <P>This will overwrite the current summary.</P>
          <div className="h-[0.5px] w-full justify-between rounded bg-border-secondary" />

          <div className="flex flex-col gap-1">
            <P>Your new note will be generated with the following template:</P>
            <div className="flex  w-full justify-between rounded-b-xl">
              <TemplateSelectorGuard
                selectedPromptTemplateId={
                  selectedTemplates?.initialPromptTemplateId ??
                  initialPromptTemplateId ??
                  undefined
                }
                selectedOutputTemplateId={
                  selectedTemplates?.initialOutputTemplateId ??
                  initialLlmOutputTemplateId ??
                  undefined
                }
                onSelectionChanged={({
                  initialPromptTemplateId,
                  initialOutputTemplateId,
                }) => {
                  setSelectedTemplates({
                    initialPromptTemplateId,
                    initialOutputTemplateId,
                  });
                }}
                usesPortal={false}
              />
            </div>
          </div>
          <DialogFooter className="w-full flex-row justify-end gap-2">
            <Button
              variant="ghost"
              className="w-fit bg-bg-quaternary hover:bg-border-primary"
              onClick={toggleDialog}
            >
              Cancel
            </Button>
            <Button onClick={handleRegenerate} className="w-fit">
              <RefreshCcw className="h-4 pr-1" />
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
